/**
 * Auth-Service — Login/Register/Refresh. Abgeleitet aus BuildScan.
 * Login/Register/Refresh nutzen rohes fetch (Auth-Endpunkte, kein 401-Retry).
 */

const API = import.meta.env.VITE_API_URL || '/api/v1';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UserResponse {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  rolle?: 'admin' | 'erfasser';
  /** true = Startpasswort aktiv, Wechsel wird erzwungen (Server + Router-Guard). */
  must_change_password?: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: UserResponse;
}

/** Login-Ergebnis: entweder Tokens (2FA aus) oder eine 2FA-Aufforderung. */
export type MfaStatus = 'mfa_required' | 'enroll_required';
export interface MfaChallenge {
  status: MfaStatus;
  mfa_token: string;
}
export type LoginResult = TokenResponse | MfaChallenge;

export interface MfaSetup {
  secret: string;
  otpauth_uri: string;
  qr_svg: string;
}
export interface ActivateResult extends TokenResponse {
  backup_codes: string[];
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Fehler ${res.status}`);
  }
  return res.json();
}

export function istTokenResponse(r: LoginResult): r is TokenResponse {
  return (r as TokenResponse).access_token !== undefined;
}

export async function login(data: LoginRequest): Promise<LoginResult> {
  const res = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse<LoginResult>(res);
}

/** Schritt 2a: Einrichtung starten — liefert Secret + QR-Code. */
export async function mfaSetup(mfaToken: string): Promise<MfaSetup> {
  const res = await fetch(`${API}/auth/2fa/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfa_token: mfaToken }),
  });
  return handleResponse<MfaSetup>(res);
}

/** Schritt 2b: Einrichtung bestätigen — liefert Tokens + einmalige Backup-Codes. */
export async function mfaActivate(mfaToken: string, code: string): Promise<ActivateResult> {
  const res = await fetch(`${API}/auth/2fa/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfa_token: mfaToken, code }),
  });
  return handleResponse<ActivateResult>(res);
}

/** Schritt 2 (bestehende 2FA): Code prüfen — liefert Tokens. */
export async function mfaVerify(mfaToken: string, code: string): Promise<TokenResponse> {
  const res = await fetch(`${API}/auth/2fa/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfa_token: mfaToken, code }),
  });
  return handleResponse<TokenResponse>(res);
}

export async function refreshToken(refreshToken: string): Promise<TokenResponse> {
  const res = await fetch(`${API}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return handleResponse<TokenResponse>(res);
}
